import { useEffect, useRef } from 'react';
import { GameStateStatus, PlayerTableViewModelV2 } from 'shared';
import { playSound, primeAudioOnUserGesture } from '../../ui/sounds';

/*
 * useGameSounds — plays soft table sounds off state transitions.
 *
 * The socket only signals; the client re-fetches the full snapshot on every
 * update (see setback.tsx). So there are no per-action events to hook — instead
 * we diff the previous snapshot against the new one and derive what happened:
 *
 *   - card played : total cards held across all hands dropped by one. (Refills
 *                   at set start only *raise* the count, so they don't fire.)
 *   - bid made    : still in Bidding, and the active bidder advanced.
 *   - set ended   : the scored-set history grew by an entry.
 *   - your turn   : the local player's seat went from not-current to current.
 *
 * All sounds are best-effort and gated behind the mute toggle inside sounds.ts.
 */

// Sum of cards still in every seat's hand — decreases by exactly one per play.
const totalCardsInHands = (table: PlayerTableViewModelV2): number =>
  table.tablePlayers.reduce((sum, p) => sum + (p?.numberOfCards ?? 0), 0);

const biddingPlayer = (table: PlayerTableViewModelV2): string | undefined => {
  const gs = table.gameState;
  return gs && gs.status === GameStateStatus.Bidding ? gs.currentBiddingPlayer : undefined;
};

const setHistoryLength = (table: PlayerTableViewModelV2): number =>
  table.gameState?.setHistory?.length ?? 0;

const localIsCurrent = (table: PlayerTableViewModelV2): boolean =>
  !!table.tablePlayers[0]?.isCurrentTurn;

export const useGameSounds = (table?: PlayerTableViewModelV2): void => {
  const prevRef = useRef<PlayerTableViewModelV2>();

  useEffect(() => {
    primeAudioOnUserGesture();
  }, []);

  useEffect(() => {
    if (!table) return;
    const prev = prevRef.current;
    prevRef.current = table;
    // First snapshot just seeds the baseline — nothing to compare against yet.
    if (!prev) return;

    // A set was scored.
    if (setHistoryLength(table) > setHistoryLength(prev)) {
      playSound('setEnd');
    }

    // A card left someone's hand.
    if (totalCardsInHands(table) < totalCardsInHands(prev)) {
      playSound('card');
    }

    // A bid/pass advanced the active bidder.
    const nowBidder = biddingPlayer(table);
    if (
      table.gameState?.status === GameStateStatus.Bidding &&
      prev.gameState?.status === GameStateStatus.Bidding &&
      nowBidder !== biddingPlayer(prev)
    ) {
      playSound('bid');
    }

    // It became the local player's turn — nudge it slightly later so it reads
    // as a distinct ding after any card that landed in the same update.
    if (localIsCurrent(table) && !localIsCurrent(prev)) {
      window.setTimeout(() => playSound('turn'), 140);
    }
  }, [table]);
};
